// components/pages/OgrenciDuyuru/OgrenciDuyuru.tsx
import React, { useState, useEffect } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    useTheme,
    useMediaQuery,
    Typography,
    CircularProgress,
    Alert,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    SelectChangeEvent,
    Card,
    CardContent,
    Divider,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../../../contexts/AuthContext';
import { format } from 'date-fns/format';
import { tr } from 'date-fns/locale';
import { DuyuruError } from '../../../types/ErrorModals/DuyuruError';
import { getOgrenciDuyuru } from '../../../services/api/ogrenciDuyuruService';

const OgrenciDuyuru: React.FC = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const { kullaniciModal } = useAuth();

    // Add state for selected season with default value 2025
    const [selectedSeason, setSelectedSeason] = useState<string>('2025');
    // Available seasons - same as in other components
    const availableSeasons = ['2023', '2024', '2025', '2026', '2027'];

    const handleSeasonChange = (event: SelectChangeEvent) => {
        setSelectedSeason(event.target.value);
    };

    const { data, isLoading, error, refetch } = useQuery({
        queryKey: ['ogrenciDuyuru', kullaniciModal?.id, selectedSeason],
        queryFn: async () => {
            try {
                return await getOgrenciDuyuru({
                    kurumKodu: localStorage.getItem('kurumKodu') || '',
                    ogrenciId: kullaniciModal?.id || '',
                    sezon: parseInt(selectedSeason)
                });
            } catch (err) {
                const duyuruError = err as DuyuruError;
                throw new Error(duyuruError.message);
            }
        },
        enabled: !!kullaniciModal?.id,
        retry: 1
    });

    // Refetch data when season changes
    useEffect(() => {
        if (kullaniciModal?.id) {
            refetch();
        }
    }, [selectedSeason, refetch, kullaniciModal?.id]);

    return (
        <>
            {/* Season Selector */}
            <Box sx={{ mb: 3, mt: 2 }}>
                <FormControl
                    variant="outlined"
                    size="small"
                    sx={{
                        minWidth: 200,
                        '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                                borderColor: theme.palette.primary.main,
                            },
                        }
                    }}
                >
                    <InputLabel id="season-select-label">Eğitim Sezonu</InputLabel>
                    <Select
                        labelId="season-select-label"
                        id="season-select"
                        value={selectedSeason}
                        onChange={handleSeasonChange}
                        label="Eğitim Sezonu"
                    >
                        {availableSeasons.map((season) => (
                            <MenuItem key={season} value={season}>
                                {season} - {(parseInt(season) + 1)} Eğitim Yılı
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>

            {isLoading ? (
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                    <CircularProgress />
                </Box>
            ) : error ? (
                <Alert
                    severity="error"
                    sx={{ mt: 2 }}
                    action={
                        process.env.NODE_ENV === 'development' && (
                            <Typography variant="caption" display="block">
                                CorrelationId: {(error as DuyuruError).correlationId}
                            </Typography>
                        )
                    }
                >
                    {(error as Error).message}
                </Alert>
            ) : !data?.length ? (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {selectedSeason} sezonunda kayıtlı öğrenci duyurusu bulunmamaktadır.
                </Alert>
            ) : (
                <Box sx={{ mt: { xs: 2, sm: 3 } }}>
                    {/* Table view for larger screens */}
                    {!isMobile ? (
                        <TableContainer component={Paper} sx={{ minWidth: 800, boxShadow: 3 }}>
                            <Table aria-label="öğrenci duyuruları tablosu">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Tarih
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Saat
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Başlık
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            İçerik
                                        </TableCell>
                                        <TableCell sx={{ fontWeight: "bold", backgroundColor: "#6e6e6e", color: "white" }}>
                                            Gönderen
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.map((duyuru, index) => (
                                        <TableRow
                                            key={index}
                                            sx={{
                                                "&:nth-of-type(odd)": { backgroundColor: "#f9f9f9" },
                                                "&:hover": {
                                                    backgroundColor: "rgba(232, 137, 16, 0.1)",
                                                    transition: "background-color 0.3s ease",
                                                },
                                            }}
                                        >
                                            <TableCell>
                                                {format(new Date(duyuru.tarih2 || new Date()), 'dd MMMM yyyy', { locale: tr })}
                                            </TableCell>
                                            <TableCell>{duyuru.displaySaat}</TableCell>
                                            <TableCell>{duyuru.aciklama}</TableCell>
                                            <TableCell>{duyuru.aciklama2}</TableCell>
                                            <TableCell>{duyuru.gorusen}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        // Card view for mobile
                        <Box>
                            {data.map((duyuru, index) => (
                                <Card
                                    key={index}
                                    sx={{
                                        mb: 2,
                                        boxShadow: 3,
                                        "&:hover": {
                                            boxShadow: 6,
                                            transition: "box-shadow 0.3s ease",
                                        },
                                    }}
                                >
                                    <CardContent>
                                        <Typography variant="h6" color="primary" gutterBottom>
                                            {duyuru.aciklama}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            {format(new Date(duyuru.tarih2 || new Date()), 'dd MMMM yyyy', { locale: tr })} - {duyuru.displaySaat}
                                        </Typography>
                                        <Divider sx={{ my: 1 }} />
                                        <Typography variant="body1" paragraph>
                                            {duyuru.aciklama2 || "Detaylı açıklama bulunmamaktadır."}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" align="right">
                                            Gönderen: {duyuru.gorusen}
                                        </Typography>
                                    </CardContent>
                                </Card>
                            ))}
                        </Box>
                    )}
                </Box>
            )}
        </>
    );
};

export default OgrenciDuyuru;